import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api, setAccessToken, getAccessToken, refreshAccessToken, captureRefreshToken, startImpersonation as installImpersonation, stopImpersonation as restoreAdminToken, isImpersonating, getImpersonation } from "@/lib/api";
import { clearRefreshToken } from "@/lib/refreshStore";

const AuthContext = createContext(null);
const LEGACY_TOKEN_KEY = "valiosia_token";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const impersonationTimer = useRef(null);

  const fetchMe = useCallback(async () => {
    const tokenAtStart = getAccessToken();
    const { data } = await api.get("/auth/me");
    // Never allow a late /me response from a prior identity to replace the
    // current user after entering or leaving an impersonation session.
    if (tokenAtStart === getAccessToken()) setUser(data);
    return data;
  }, []);

  // Mid-session expiry (refresh cookie dead): api.js emits this event once —
  // clearing the user makes ProtectedRoute route back to /login cleanly.
  useEffect(() => {
    const onExpired = () => setUser(null);
    const onImpersonationExpired = () => {
      // The regular admin access token was retained only in memory. Do not
      // touch refresh storage: returning is a local token swap, not a login.
      fetchMe().catch(() => setUser(null));
    };
    window.addEventListener("valiosia:session-expired", onExpired);
    window.addEventListener("valiosia:impersonation-expired", onImpersonationExpired);
    return () => {
      window.removeEventListener("valiosia:session-expired", onExpired);
      window.removeEventListener("valiosia:impersonation-expired", onImpersonationExpired);
    };
  }, [fetchMe]);

  useEffect(() => () => {
    if (impersonationTimer.current) clearTimeout(impersonationTimer.current);
  }, []);

  // Boot: mint an access token from the refresh cookie, then load /auth/me.
  // Legacy localStorage token is read once for migration only (users who logged
  // in before the in-memory switch) and never written again.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        try {
          await refreshAccessToken();
        } catch {
          const legacy = localStorage.getItem(LEGACY_TOKEN_KEY);
          if (legacy) setAccessToken(legacy);
        }
        if (getAccessToken()) await fetchMe();
        else if (alive) setUser(null);
      } catch {
        setAccessToken(null);
        if (alive) setUser(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setAccessToken(data.access_token || data.token || null);
    captureRefreshToken(data);
    setUser(data.user);
    return data.user;
  };

  // Magic-link sign-in (§9.1): exchange the emailed token for a session. Same
  // response shape as /auth/login, so access_status gating is honoured downstream.
  const loginWithMagic = async (token) => {
    const { data } = await api.post("/auth/magic-login", { token });
    setAccessToken(data.access_token || data.token || null);
    captureRefreshToken(data);
    setUser(data.user);
    return data.user;
  };

  // Password reset (esqueci a senha): consume the emailed token + new password.
  // Same response shape as /auth/login, so the user lands already signed in.
  const resetPassword = async (token, password) => {
    const { data } = await api.post("/auth/reset-password", { token, password });
    setAccessToken(data.access_token || data.token || null);
    captureRefreshToken(data);
    setUser(data.user);
    return data.user;
  };

  const signup = async (name, email, password) => {
    const { data } = await api.post("/auth/signup", { name, email, password });
    setAccessToken(data.access_token || data.token || null);
    captureRefreshToken(data);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    if (impersonationTimer.current) clearTimeout(impersonationTimer.current);
    if (isImpersonating()) restoreAdminToken();
    try { await api.post("/auth/logout"); } catch { /* best-effort */ }
    setAccessToken(null);
    clearRefreshToken();
    localStorage.removeItem(LEGACY_TOKEN_KEY);
    setUser(null);
  };

  // Switch the active workspace context (Epic 10). `ownerUserId = null` returns
  // to the user's own account. Server resolves the grant, then we refetch
  // /auth/me so `active_workspace` / read-only gating updates everywhere.
  const switchWorkspace = useCallback(async (ownerUserId) => {
    await api.post("/collaborators/switch", { owner_user_id: ownerUserId ?? null });
    return fetchMe();
  }, [fetchMe]);

  const refresh = fetchMe;

  const startImpersonation = async (targetId) => {
    const { data } = await api.post(`/admin/users/${targetId}/impersonate`);
    installImpersonation({ token: data.token, user: data.user, expiresAt: data.expires_at });
    if (impersonationTimer.current) clearTimeout(impersonationTimer.current);
    const remaining = Math.max(0, new Date(data.expires_at).getTime() - Date.now());
    impersonationTimer.current = window.setTimeout(() => {
      if (restoreAdminToken()) {
        fetchMe().catch(() => setUser(null));
        window.dispatchEvent(new Event("valiosia:impersonation-expired"));
      }
    }, remaining);
    // Trust the public user returned by the administrative endpoint. Fetching
    // /auth/me here is optional and could race a token swap in older browsers.
    setUser(data.user);
    return data;
  };

  const stopImpersonation = async () => {
    if (impersonationTimer.current) clearTimeout(impersonationTimer.current);
    if (!restoreAdminToken()) return user;
    return fetchMe();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithMagic, resetPassword, signup, logout, refresh, switchWorkspace, startImpersonation, stopImpersonation, impersonation: getImpersonation() }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth deve estar dentro de AuthProvider");
  return ctx;
}
